import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    Input,
    OnInit,
    ViewChild
} from '@angular/core';
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import {FeedPost, Post, PostComment} from '../../_types/content';
import {videoLinkUpload} from '../../_utils/video-link-sanitizer';
import {environment} from '../../../environments/environment';
import {Heatmap} from '../../_types/polls';
import {User} from "../../_types/user";
import {AuthService} from "../../_services/auth.service";
import {ContentService} from "../../_services/content.service";
import {Community, UserAccessLevel} from "../../_types/communities";
import {CommunitiesService} from "../../_services/communities.service";
import {LocalStorageService} from "../../_services/local-storage.service";
import {MatMenuTrigger} from "@angular/material/menu";

@Component({
    selector: '' +
        'app-post-content',
    templateUrl: './post-content.component.html',
    styleUrls: ['./post-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PostContentComponent implements OnInit, AfterViewInit {
    @Input({required: true}) content!: Post | PostComment | FeedPost;
    @Input() disableOverlay: boolean = false;
    @Input() hideAttachments: boolean = false;
    surveyCompleted: boolean = false;
    currentUser: User;
    show: boolean = false;
    displayExpandContent: boolean = true;
    videoPlayed: boolean = false;
    selectedMenuUser: User | null = null;
    @ViewChild('image') image: ElementRef | undefined;
    @ViewChild('textContent') textContent: ElementRef | undefined;
    @ViewChild('selectedMenuTrigger') selectedMenuTrigger: MatMenuTrigger | undefined;
    @HostBinding('class.comment-content') myCustomClass = false;

    constructor(private domSanitizer: DomSanitizer,
                private cdr: ChangeDetectorRef,
                private elementRef: ElementRef,
                private contentService: ContentService,
                private authService: AuthService,
                private readonly communitiesService: CommunitiesService,
                private readonly localStorageService: LocalStorageService,
    ) {
        this.currentUser = this.authService.getUser();
    }

    @Input() set isCommentStyle(value: boolean) {
        this.myCustomClass = value;
    };

    get formattedText(): string {
        // Define a regex pattern to match <a> tags without target="_blank"
        const pattern: RegExp = /(<a\s+(?![^>]*target=)[^>]*)(>)/gi;
        // Replace each match with the modified tag, adding target="_blank"
        return this.content.text.replace(pattern, '$1 target="_blank"$2');
    }

    get heatmap(): Heatmap | null {
        return (this.hasHeatmap(this.content) && this.content?.heatmap_surveys?.[0]) || null;
    }

    ngOnInit(): void {
        if (this.content?.attachments) {
            this.setSafeLinks();
            for (let attachment of this.content?.attachments) {
                if (attachment.attachment_type == 'survey_url') {
                    if (this.hasCommunityRole(['member']) || this.isSuperUser()) {
                        this.contentService.getSurveyStatus(this.content.id).subscribe(r => {
                            this.surveyCompleted = r.data.survey_completed
                            this.cdr.detectChanges();
                        }, e => {
                            console.error(e)
                        })
                    } else {
                        this.surveyCompleted = true;
                    }
                }
            }
        }
    }

    isSuperUser(): boolean {
        return this.authService.isSuperUser();
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.contentService.userHasCommunityRole(roles);
    }

    ngAfterViewInit(): void {

        this.checkTextContentHeight();
        this.cdr.detectChanges();
        const nativeElement: any = this.elementRef.nativeElement;
        const mentions: HTMLElement[] = nativeElement.getElementsByClassName("ngx-text-highlight-tag");

        for (const mention of Array.from(mentions)) {
            mention.addEventListener('click', (): void => {
                this.mentionClick(mention as HTMLElement);
            });
        }
    }

    mentionClick(mention: Element): void {
        this.selectedMenuUser = null
        let username = mention.getAttribute('data-username')
        if (username) {

            let selectedCommunity = this.localStorageService.getItem<Community | null>(
                'lastSelectedCommunity',
                null
            );
            this.currentUser = this.authService.getUser();
            if (selectedCommunity?.id) {
                this.communitiesService.searchUserList(selectedCommunity.id, username).subscribe(r => {
                    if (r[0] && this.selectedMenuTrigger) {
                        this.selectedMenuUser = r[0].user;
                        // @ts-ignore weird issue where it isn't registering as a matmenutrigger
                        if (this.selectedMenuTrigger.nativeElement) {
                            // @ts-ignore
                            this.selectedMenuTrigger?.nativeElement.click();
                        } else {
                            this.selectedMenuTrigger?.openMenu();
                        }
                    }
                }, e => {
                    console.error(e)
                })
            }

        }
    }

    get_url_extension(url: string): string | undefined {
        if (url) {
            return url.split(/[#?]/)[0].split('.').pop()?.trim();
        } else {
            return undefined
        }
    }

    openSurvey(url: string): void {
        let urlWithParams: string = url + '?id=' + this.currentUser?.id + '&survey=' + this.content?.id + '&returnUrl=' + window.location.protocol + window.location.host + '/feed'
        this.contentService.getCurrentDashboardCommunity().subscribe(r => {
            if (r?.id) {
                this.contentService.reportAction('external-survey-open', 'post', this.content.id, r.id).subscribe(r => {
                    window.location.href = urlWithParams;
                }, e => {
                    window.location.href = urlWithParams;
                })
            } else {
                window.location.href = urlWithParams;
            }
        });
    }

    reportAction(url?: string): void {
        if (!this.videoPlayed && (!url || url.includes('youtube') || url.includes('vimeo'))) {
            this.videoPlayed = true
            this.contentService.getCurrentDashboardCommunity().subscribe(r => {
                let postId = null;
                if (this.content as Post) {
                    postId = this.content.id;
                }
                this.contentService.reportAction('video-play', 'post', postId, r?.id, url ? 'Video URL: ' + url : null).subscribe(r => {
                })
            });
        }
    }

    private setSafeLinks(): void {
        for (let attachment of this.content?.attachments) {
            if (attachment.attachment_type === 'video_url') {

                attachment.isApiUrl = attachment.url.includes(environment.hostUrl);
                attachment.safeVideoLink = videoLinkUpload(attachment.url, this.domSanitizer)?.link;
            } else if (attachment.attachment_type === 'video') {
                attachment.isApiUrl = attachment.file.includes(environment.hostUrl);
                let url: SafeResourceUrl | undefined = videoLinkUpload(attachment.file, this.domSanitizer)?.link;
                if (url) {
                    attachment.videoUrl = url
                }
            } else if (attachment.attachment_type === 'gif_url') {
                attachment.safeImageLink = videoLinkUpload(attachment.url, this.domSanitizer)?.link;
            }
        }

    }

    private checkTextContentHeight(): void {
        if (!this.disableOverlay) {

            if (this.textContent && this.textContent.nativeElement.offsetHeight < 200) {
                this.show = true;
                this.displayExpandContent = false;
            } else {
                this.show = false;
                this.displayExpandContent = true;
            }
        } else {
            this.show = true;
            this.displayExpandContent = false;
        }
    }

    private hasHeatmap(content: Post | PostComment | FeedPost): content is Post | FeedPost {
        return (content as Post | FeedPost).heatmap_surveys !== undefined;
    }

}
